import useTranslate from "../../../core/languages/hooks/useTranslate";
import React, { useEffect, useRef, useState } from "react";
import ClearDataButton from "./ClearDataButton";
import { BookingFilters } from "../../../booking/hooks/useBookingMeSearchData";
import {useCityMeSearchData} from "../../../booking/hooks/useCityMeSearchData";
import useCustomerContext from "../../../customers/hooks/useCustomerContext";

interface Props {
    onChange: (updates: Partial<BookingFilters>) => void;
    cities: { id: string | number; name: string }[];
}
const CityFilter: React.FC<Props> = ({ onChange, cities = [] }) => {
    const t = useTranslate();
    const cityRef = useRef<HTMLDivElement | null>(null);
    const [showCityPopover, setShowCityPopover] = useState(false);
    const [selectedCity, setSelectedCity] = useState<string | null>(null);
    const { data: cityObjects, loading, fetchData } = useCityMeSearchData();
    const {languageCode} = useCustomerContext();

    const handleSelectCity = (value: string | null) => {
        setSelectedCity(value);
        setShowCityPopover(false);

        if (value) {
            onChange({ city: value });
        } else {
            onChange({ city: undefined });
        }
    };

    const handleClickOutside = (e: MouseEvent) => {
        if (cityRef.current && !cityRef.current.contains(e.target as Node)) {
            setShowCityPopover(false);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        if (!cities || cities.length === 0) return;

        const cityIds = cities.map((e) => e?.toString().split("/").pop()).filter(Boolean);

        fetchData({ city: cityIds });
    }, [cities, fetchData]);

    const renderPopoverOptions = (
        options: { label: string; value: string | number }[],
        onSelect: (value: string | null) => void
    ) =>
        options.map((opt) => (
            <div
                key={opt.value}
                className="flex px-4 sm:px-8 items-center py-4 hover:bg-neutral-100 dark:hover:bg-neutral-700 cursor-pointer"
                onClick={() => onSelect(String(opt.value))}
            >
                <span className="block font-medium text-neutral-700 dark:text-neutral-200">
                    {opt.label}
                </span>
            </div>
        ));

    const optionsCities = (cityObjects ?? []).map(e => ({
        label: e?.trName[languageCode] ?? "-",
        value: e?.id ?? "",
    }));

    return (
        <div
            ref={cityRef}
            className="relative w-60 cursor-pointer [ nc-hero-field-padding ] items-center min-w-0"
        >
            <div className="flex items-start">
                <div className="flex-grow min-w-0">
                    <div
                        className="w-full p-0 xl:text-lg font-semibold min-w-0 cursor-pointer text-neutral-800 dark:text-neutral-100"
                        onClick={() => setShowCityPopover((v) => !v)}
                    >
                        {selectedCity ? (
                            <span className="whitespace-normal break-words block">
                                {(cityObjects ?? []).find((e) => String(e.id) === selectedCity)?.trName?.[languageCode] ??
                                    t("bookingMeForm.city")}
                            </span>
                        ) : (
                            <span className="whitespace-normal break-words block text-neutral-800 dark:text-neutral-200">
                                {t("bookingMeForm.city")}
                            </span>
                        )}
                    </div>
                    <span className="block mt-0.5 text-sm text-neutral-400 font-light">
                        {t("bookingMeForm.searchByCity")}
                    </span>
                </div>

            {selectedCity && <ClearDataButton onClick={() => handleSelectCity(null)} />}

            {showCityPopover && !loading && (
                <div className="absolute left-0 z-40 w-full bg-white dark:bg-neutral-800 top-full mt-3 py-3 rounded-3xl shadow-xl max-h-60 overflow-y-auto">
                    {renderPopoverOptions(optionsCities, handleSelectCity)}
                </div>
            )}
        </div>
        </div>
    );
};

export default CityFilter;
